import * as Sentry from "@sentry/react";
import type { ErrorEvent, EventHint } from "@sentry/react";
import { createDedupeState, shouldSend, type DedupeState } from "@/lib/sentryDedupe";
import { sentryDsnProblem } from "@/lib/sentryDsn";
import { PUSH_OPTIN_EVENT } from "@/lib/pushOptinEvents";
import { LIVE_AUDIO_EVENT } from "@/lib/liveAudioTelemetry";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

/**
 * Estado da dedupe, por sessão de app. Fora do initSentry porque `beforeSend`
 * é chamado a cada evento e precisa do mesmo mapa entre chamadas.
 */
const dedupe = createDedupeState();

// ── Analítica via captureMessage NÃO é erro (task f04c14ca) ─────────────────
// Os push_optin_* são o único funil que enxerga o app nativo (o PostHog é
// cego lá, ~85% da base recente) e continuam sendo ENVIADOS — o objetivo é
// que deixem de parecer erro, não que sumam. A fonte dos nomes é
// PUSH_OPTIN_EVENT, importada: lista literal duplicada aqui sairia de
// sincronia com os call sites no primeiro evento novo.
// LOTE 5 — a sonda de áudio do LIVE entra na MESMA faixa, e pelo mesmo
// motivo dos push_optin_*: é medida, não erro, e precisa CONTAR por sessão.
// Passar antes da dedupe é o ponto — a mensagem é sempre a mesma string, e a
// janela de 60 s engoliria a segunda sessão de LIVE de quem testa duas
// seguidas, que é exatamente o caso de uso do aceite.
const ANALYTICS_EVENT_NAMES: ReadonlySet<string> = new Set<string>([
  ...Object.values(PUSH_OPTIN_EVENT),
  LIVE_AUDIO_EVENT,
]);

// Higiene de PII compartilhada pelos DOIS caminhos de saída do beforeSend
// (analítica e erro) — um evento de analítica também carrega breadcrumbs.
function stripPii(event: ErrorEvent): ErrorEvent {
  // Strip request body globally — protects mirror-chat user messages
  if (event.request) {
    delete event.request.data;
    delete event.request.cookies;
  }
  // Strip ANY breadcrumb data field (network bodies, etc.)
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((b) => ({
      ...b,
      data: b.data ? { url: b.data.url, status: b.data.status_code } : undefined,
    }));
  }
  return event;
}

// Exportada (em vez de inline no init) pelo mesmo motivo do sentryDedupe:
// a ORDEM dos ramos é comportamento de produção e precisa de teste —
// analítica depois do filtro de ruído e ANTES da dedupe, ver comentários.
// `state`/`nowMs` só existem para o teste injetar; produção usa os defaults.
export function sentryBeforeSend(
  event: ErrorEvent,
  hint: EventHint,
  state: DedupeState = dedupe,
  nowMs: number = Date.now(),
): ErrorEvent | null {
  // Filter Supabase auth LockManager noise (multi-tab, non-fatal).
  // @supabase/auth-js uses navigator.locks to serialize token refresh
  // across tabs; the losing tab gets a non-actionable AbortError.
  const errorMessage =
    event.exception?.values?.[0]?.value ||
    hint?.originalException?.toString() ||
    "";
  if (
    errorMessage.includes("Lock was stolen") ||
    errorMessage.includes("lock:sb-") ||
    errorMessage.includes("was released because another request stole it")
  ) {
    return null;
  }

  // ── ANALÍTICA (push_optin_*): força info e sai ANTES da dedupe ──────────
  // Depois do LockManager (ruído morre primeiro, sem gastar nada) e antes
  // da dedupe POR DESENHO: funil se CONTA — a janela de 60s engoliria
  // repetição legítima (dois shown na mesma sessão) e cada evento gastaria
  // chave do mapa (teto de 200), roubando espaço de erro real que
  // compartilhe a janela. O level "info" daqui é cinto e suspensório: os
  // call sites já mandam "info" explícito, mas um call site novo que
  // esqueça o level não pode reintroduzir a task f04c14ca.
  if (typeof event.message === "string" && ANALYTICS_EVENT_NAMES.has(event.message)) {
    event.level = "info";
    return stripPii(event);
  }

  // ── DEDUPE por mensagem ─────────────────────────────────────────────
  // Com o captureConsole ligado, um console.error dentro de um laço
  // (retry, polling, render de lista) vira mil eventos idênticos em
  // segundos. A mesma mensagem passa UMA vez por janela; o primeiro de
  // cada mensagem sempre passa, então erro novo nunca é engolido.
  // Roda depois do filtro de ruído do LockManager, para não gastar chave
  // do mapa com evento que já ia ser descartado.
  if (!shouldSend(state, errorMessage || event.message || "", nowMs)) {
    return null;
  }

  return stripPii(event);
}

export function initSentry() {
  const problema = sentryDsnProblem(DSN);
  if (problema) {
    // INCONDICIONAL, não só em DEV. Era `if (import.meta.env.DEV)` — o mesmo
    // bug que deixou o PostHog mudo no app nativo (task b48e957d), e aqui
    // pior: o Sentry é a ÚNICA ferramenta que enxerga o WebView, onde vivem
    // 70-85% do produto. DSN vazio = perder a última janela para a maioria
    // dos usuários, e o PostHog não serve de rede porque já é cego lá.
    //
    // O CANAL É O CONSOLE, e só ele pode ser: o Sentry não tem como anunciar
    // a própria ausência via Sentry, e o PostHog está DEPOIS desta chamada no
    // main.tsx (nem inicializado ainda) além de cego no nativo — alarme
    // cruzado entre os dois seria circular e frágil. Console incondicional é
    // o que sobra, e é o que faz um logcat responder "este build tem Sentry?"
    // sem adivinhação.
    console.warn(
      problema === "missing"
        ? "[sentry] VITE_SENTRY_DSN ausente — Sentry DESLIGADO (nenhum erro será reportado)"
        : "[sentry] VITE_SENTRY_DSN não parece um DSN (https://<key>@<host>/<id>) — o SDK vai recusar",
    );
    // `missing` não tem o que inicializar. `invalid` SEGUE para o init de
    // propósito: se esta regex um dia errar sobre um DSN bom, ela não pode
    // ser a linha que nos cega — o SDK aceita o que presta e reclama alto do
    // que não presta. O grito acima já cumpriu o papel do alarme.
    if (problema === "missing") return;
  }

  Sentry.init({
    dsn: DSN,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: false,
      }),
      // ── console.error VIRA EVENTO (NANO 29/07) ──────────────────────────
      // Até aqui a §11 do CLAUDE.md mandava "nunca silencioso, sempre
      // console.error antes do fallback" — e o console.error não chegava a
      // lugar nenhum: sem esta integração ele é breadcrumb, nunca evento. A
      // regra e a configuração estavam em oposição, e o custo foi concreto:
      // "Sentry limpo nas últimas 3h" foi usado como prova de que nenhuma
      // exceção tinha ocorrido no bug do compositor. Não provava nada.
      //
      // SÓ `error`. `warn` fica de fora até medir o volume real: são 563
      // console.error e 90 console.warn em src/, e ligar os dois de uma vez
      // afogaria o sinal antes de a gente saber com o que está lidando.
      //
      // ── A REGRA QUE NASCE COM ISTO: error vs warn ───────────────────────
      // A partir daqui `console.error` NÃO é mais só um log — é uma decisão de
      // criar um evento. Dois critérios, nesta ordem:
      //
      //  1. Já existe `Sentry.captureException` no MESMO handler? Então o
      //     console vai em `warn`. A captura explícita é o evento (ela tem
      //     `tags.feature`; o evento de console não teria) e a linha de console
      //     vira breadcrumb ANEXADO a ela. Sem esta regra o par vira DOIS
      //     eventos não correlacionados — a dedupe por mensagem daqui de baixo
      //     não junta os dois, porque as mensagens são diferentes. Eram 26
      //     sites assim quando esta integração foi ligada.
      //  2. É falha esperada com fallback REAL (contador decorativo que cai
      //     para 0, refresh de fundo do que já está na tela, estado normal como
      //     "ainda não tem usuário")? `warn`. O teste é: **o fallback preserva
      //     o que o usuário veio fazer, ou ele MENTE?** Lista que falha e
      //     renderiza vazia mente — mostra "nada aqui" para quem tem coisas —
      //     e essa continua `error`.
      Sentry.captureConsoleIntegration({ levels: ["error"] }),
    ],
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    sendDefaultPii: false,
    // Corpo extraído para sentryBeforeSend (exportada) — a ordem dos ramos
    // (LockManager → analítica info → dedupe → strip PII) é testada em
    // src/test/pushOptinSentryLevel.test.ts.
    beforeSend(event, hint) {
      return sentryBeforeSend(event, hint);
    },
  });
}

export { Sentry };
